import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ChatMessage, ViewMode, User, ApiResponse } from './types/index';
import { apiService } from './services/api';
import { X } from 'lucide-react';
import Sidebar from './components/Sidebar';
import ChatInterface from './components/ChatInterface';
import DocumentEditor from './components/DocumentEditor';
import Header from './components/Header';
import ScanAnalysis from './components/ScanAnalysis';
import ScanAnalysisPage from './components/ScanAnalysisPage';
import DeepResearch from './components/DeepResearch';
import TreatmentPlanDemo from './components/TreatmentPlanDemo';
import AskQuestionsInterface from './components/AskQuestionsInterface';
import ScanHistory from './components/ScanHistory';
import WelcomeScreen from './components/WelcomeScreen';
import { TreatmentPlanProvider, useTreatmentPlan } from './contexts/TreatmentPlanContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import logger from './utils/logger';
import { generateShortId } from './utils/hashId';
import { formatCaseCriteria, hashSessionId } from './utils/caseCriteriaMapper';
import { Analytics } from '@vercel/analytics/react';
import { initPostHog, orthoTrack, identifyUser, resetUser } from './utils/posthog';

// Auth components
import SignIn from './components/auth/SignIn';
import SignUp from './components/auth/SignUp';
import ResetPassword from './components/auth/ResetPassword';
import ProtectedRoute from './components/ProtectedRoute';

function AppContent() {
  // Feature configuration matching Sidebar
  const featureConfig = {
    showTreatmentPlan: true,
    showSmartScan: true,
    showDeepResearch: false,  // Deep Research is hidden
    showTreatmentDemo: true,
  };

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('split');
  const [backendConnected, setBackendConnected] = useState<boolean | null>(null);
  const [apiLogs, setApiLogs] = useState<string[]>([]);
  const [showApiLogs, setShowApiLogs] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [selectedTreatment, setSelectedTreatment] = useState<{ option: number; label: string } | null>(null);
  const [showScanAnalysis, setShowScanAnalysis] = useState(false);
  const [showScanPage, setShowScanPage] = useState(false);
  const [showDeepResearch, setShowDeepResearch] = useState(false);
  const [showTreatmentDemo, setShowTreatmentDemo] = useState(false);
  const [showTreatmentPlan, setShowTreatmentPlan] = useState(false);
  const [showDraftTreatmentPlan, setShowDraftTreatmentPlan] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [showScanHistory, setShowScanHistory] = useState(false);
  const [showAskQuestions, setShowAskQuestions] = useState(false);
  const [lastCriteriaUpdate, setLastCriteriaUpdate] = useState<Date | null>(null);
  
  const { addTreatmentPlan, setCurrentTreatmentPlan, treatmentPlans, updateTreatmentPlan, currentTreatmentPlan } = useTreatmentPlan();
  const { user: authUser, isAuthenticated } = useAuth();

  const initializeApp = useCallback(async () => {
    // Initialize PostHog analytics
    initPostHog();
    
    // Check backend connection
    const isConnected = await apiService.checkHealth();
    setBackendConnected(isConnected);

    // Get user from auth context or API
    if (authUser) {
      const userData = {
        id: authUser.user_id,
        name: authUser.name,
        email: authUser.email,
        role: authUser.role,
      };
      setUser(userData);
      // IMPORTANT: Ensure API service has the user ID for session creation
      apiService.setUserId(authUser.user_id);
      // Identify user in PostHog
      identifyUser(userData.id, {
        email: userData.email,
        name: userData.name,
        role: userData.role,
      });
    } else {
      const currentUser = await apiService.getCurrentUser();
      setUser(currentUser);
      // IMPORTANT: Ensure API service has the user ID for session creation
      // Even for test users, we need to set this for session creation to work
      apiService.setUserId(currentUser.id);
      if (currentUser.id !== 'test-user') {
        identifyUser(currentUser.id, {
          email: currentUser.email,
          name: currentUser.name,
          role: currentUser.role,
        });
      }
    }
    
    // Don't create a session on initialization - let Welcome Screen show first
    // Session will be created when user selects an action
    
    if (isConnected) {
      logger.log('✅ Backend connected successfully');
    } else {
      logger.warn('⚠️ Backend connection failed, running in offline mode');
    }
  }, [authUser, currentSessionId]);

  useEffect(() => {
    initializeApp();
  }, [initializeApp]);

  // Watch for messages that indicate criteria updates or treatment options
  const prevMessagesLength = useRef(messages.length);
  
  useEffect(() => {
    // Only check if we have NEW messages (not just re-renders)
    if (messages.length > 0 && messages.length > prevMessagesLength.current) {
      const lastMessage = messages[messages.length - 1];
      
      // Check if the last message indicates criteria update or treatment options
      if (lastMessage.sender === 'bot' && 
          (lastMessage.metadata?.criteria_updated || 
           lastMessage.metadata?.treatment_options_provided ||
           lastMessage.metadata?.action === 'criteria_complete_with_options' ||
           (lastMessage.content && 
            (lastMessage.content.includes('Treatment Options:') ||
             lastMessage.content.includes('criteria') && lastMessage.content.includes('updated'))))) {
        
        logger.log('🎆 Detected criteria update or treatment options in message');
        setLastCriteriaUpdate(new Date());
        
        // Auto-update document if we have a session and treatment plan
        if (currentSessionId && currentTreatmentPlan) {
          updateDocumentWithLatestCriteria(currentSessionId);
        }
      }
    }
    prevMessagesLength.current = messages.length;
  }, [messages, currentSessionId, currentTreatmentPlan]);

  const sendMessage = async (content: string, sessionId?: string | null) => {
    if (!content.trim()) return;

    const userMessage: ChatMessage = {
      id: generateShortId(sessionId || `temp-${Date.now()}`),
      content,
      sender: 'user',
      timestamp: new Date(),
    };

    // Update UI immediately with user message
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    
    // Track chat message sent
    orthoTrack.chatMessageSent('text', messages.length + 1);
    
    // Check if user is selecting a treatment option (e.g., "Option 1", "1", "I choose option 1", etc.)
    const treatmentSelectionPattern = /(?:option|treatment|choice|select|choose|prefer)\s*(?:#)?\s*(\d+)|^(\d+)$/i;
    const match = content.match(treatmentSelectionPattern);
    
    if (match) {
      const optionNumber = match[1] || match[2];
      if (optionNumber) {
        logger.log(`🎯 User selected treatment option: ${optionNumber}`);
        setSelectedTreatment({ option: parseInt(optionNumber), label: `Option ${optionNumber}` });
        
        // Trigger plan fetch after a short delay to allow the chat response to process
        setTimeout(async () => {
          const sessionIdToUse = sessionId || currentSessionId;
          if (sessionIdToUse) {
            try {
              logger.log(`📋 Fetching treatment plan after option selection...`);
              const planData = await apiService.getTreatmentPlan(sessionIdToUse);
              
              if (planData && currentTreatmentPlan) {
                // Update the document with the fetched plan
                const caseCriteriaData = await apiService.getCaseCriteria(sessionIdToUse).catch(() => null);
                const formattedContent = formatTreatmentPlanFromAPI(planData, sessionIdToUse, caseCriteriaData);
                
                updateTreatmentPlan(currentTreatmentPlan.id, formattedContent);
                logger.log('✅ Document updated with treatment plan after option selection');
              }
            } catch (error) {
              logger.error('Failed to fetch plan after option selection:', error);
            }
          }
        }, 2000); // Wait 2 seconds for chat response to be processed
      }
    }

    try {
      // If sessionId is provided, set it
      if (sessionId) {
        setCurrentSessionId(sessionId);
      }

      // Call API
      const response = await apiService.sendMessage(content);
      
      // Log the entire response to see what we're getting
      console.log('🔍 Full API Response:', response);
      console.log('🔍 Response keys:', Object.keys(response));
      console.log('🔍 Treatment response field:', response.treatment_response);
      console.log('🔍 Metadata:', response.metadata);
      console.log('🔍 Metadata keys:', response.metadata ? Object.keys(response.metadata) : 'no metadata');

      // Check if this is a criteria complete with treatment response (two messages)
      if (response.action === 'criteria_complete_with_treatment' || 
          response.action === 'vision_confirmed_complete_with_treatment') {
        
        // Log the full response to debug
        logger.log('📊 Criteria complete with treatment response:', {
          action: response.action,
          hasResponse: !!response.response,
          hasTreatmentResponse: !!response.treatment_response,
          treatmentResponseLength: response.treatment_response?.length,
          treatmentResponsePreview: response.treatment_response?.substring(0, 200)
        });
        
        console.log('🎯 Treatment response value:', response.treatment_response);
        
        // Add the confirmation message first
        const confirmationMessage: ChatMessage = {
          id: generateShortId(response.session_id || currentSessionId || `confirm-${Date.now()}`),
          content: response.response || 'Great! I\'ve recorded all the key details.',
          sender: 'bot',
          timestamp: new Date(),
          metadata: { ...response.metadata, isConfirmation: true },
        };
        
        // Add the treatment plan message second - make sure we're getting the treatment_response
        const treatmentContent = response.treatment_response || response.metadata?.treatment_response || '✅ Your Comprehensive Treatment Plan is Ready!';
        
        const treatmentMessage: ChatMessage = {
          id: generateShortId(response.session_id || currentSessionId || `treatment-${Date.now()}`),
          content: treatmentContent,
          sender: 'bot',
          timestamp: new Date(Date.now() + 1), // Slightly later timestamp
          metadata: { ...response.treatment_metadata, isTreatmentPlan: true },
        };
        
        setMessages(prev => [...prev, confirmationMessage, treatmentMessage]);
        
        // Fetch the full treatment plan and case criteria from the API
        const sessionIdToUse = response.session_id || currentSessionId;
        if (sessionIdToUse) {
          try {
            // Fetch both treatment plan and case criteria in parallel
            const [treatmentPlanData, caseCriteriaData] = await Promise.all([
              apiService.getTreatmentPlan(sessionIdToUse),
              apiService.getCaseCriteria(sessionIdToUse).catch(err => {
                logger.warn('Could not fetch case criteria:', err);
                return null;
              })
            ]);
            
            logger.log('📄 Fetched treatment plan:', treatmentPlanData);
            logger.log('📋 Fetched case criteria:', caseCriteriaData);
            
            // Generate treatment plan document with the fetched data
            const treatmentPlanId = generateShortId(sessionIdToUse || `plan-${Date.now()}`);
            const treatmentPlan = {
              id: treatmentPlanId,
              title: `Treatment Plan - ${new Date().toLocaleDateString()}`,
              content: formatTreatmentPlanFromAPI(treatmentPlanData, sessionIdToUse, caseCriteriaData),
              createdAt: new Date(),
              sessionId: sessionIdToUse || 'default',
            };
            
            // Add to treatment plans and set as current
            addTreatmentPlan(treatmentPlan);
            setCurrentTreatmentPlan(treatmentPlan);
            
            // If in split view, the document will auto-update
            // If not in split view, optionally switch to split view to show the document
            if (viewMode === 'chat') {
              setViewMode('split');
            }
          } catch (error) {
            logger.error('Failed to fetch treatment plan:', error);
            // Fallback to using the response content
            const treatmentPlanId = generateShortId(sessionIdToUse || `plan-${Date.now()}`);
            const treatmentPlan = {
              id: treatmentPlanId,
              title: `Treatment Plan - ${new Date().toLocaleDateString()}`,
              content: formatTreatmentPlanForDocument(treatmentContent, response),
              createdAt: new Date(),
              sessionId: sessionIdToUse || 'default',
            };
            
            addTreatmentPlan(treatmentPlan);
            setCurrentTreatmentPlan(treatmentPlan);
            
            if (viewMode === 'chat') {
              setViewMode('split');
            }
          }
        }
      } else {
        // Regular single message response
        const botMessage: ChatMessage = {
          id: generateShortId(response.session_id || currentSessionId || `temp-${Date.now()}`),
          content: response.response || response.message || 'I understand. How else can I help you?',
          sender: 'bot',
          timestamp: new Date(),
          metadata: response.metadata,
        };

        setMessages(prev => [...prev, botMessage]);
        
        // Check if this message contains treatment options (before treatment plan)
        if (response.metadata?.treatment_options_provided || 
            response.action === 'criteria_complete_with_options' ||
            (response.response && response.response.includes('Treatment Options:'))) {
          
          logger.log('🎯 Treatment options detected, preparing document...');
          const sessionIdToUse = response.session_id || currentSessionId;
          
          if (sessionIdToUse) {
            // Create or update document with criteria and options
            await updateDocumentWithLatestCriteria(sessionIdToUse);
          }
        }
        // Check if this message contains a full treatment plan
        else if (response.metadata?.isTreatmentPlan || 
                 (response.response && response.response.includes('Treatment Plan') && response.response.includes('Treatment Decisions:'))) {
          
          // Fetch the full treatment plan from the API
          const sessionIdToUse = response.session_id || currentSessionId;
          if (sessionIdToUse) {
            try {
              // Fetch both treatment plan and case criteria in parallel
              const [treatmentPlanData, caseCriteriaData] = await Promise.all([
                apiService.getTreatmentPlan(sessionIdToUse),
                apiService.getCaseCriteria(sessionIdToUse).catch(err => {
                  logger.warn('Could not fetch case criteria:', err);
                  return null;
                })
              ]);
              
              logger.log('📄 Fetched treatment plan for document:', treatmentPlanData);
              logger.log('📋 Fetched case criteria for document:', caseCriteriaData);
              
              // Generate treatment plan document with the fetched data
              const treatmentPlanId = generateShortId(sessionIdToUse || `plan-${Date.now()}`);
              const treatmentPlan = {
                id: treatmentPlanId,
                title: `Treatment Plan - ${new Date().toLocaleDateString()}`,
                content: formatTreatmentPlanFromAPI(treatmentPlanData, sessionIdToUse, caseCriteriaData),
                createdAt: new Date(),
                sessionId: sessionIdToUse || 'default',
              };
              
              // Add to treatment plans and set as current
              addTreatmentPlan(treatmentPlan);
              setCurrentTreatmentPlan(treatmentPlan);
              
              // Switch to split view to show the document
              if (viewMode === 'chat') {
                setViewMode('split');
              }
            } catch (error) {
              logger.error('Failed to fetch treatment plan:', error);
              // Fallback to using the response content
              const treatmentPlanId = generateShortId(sessionIdToUse || `plan-${Date.now()}`);
              const treatmentPlan = {
                id: treatmentPlanId,
                title: `Treatment Plan - ${new Date().toLocaleDateString()}`,
                content: formatTreatmentPlanForDocument(response.response || response.message || '', response),
                createdAt: new Date(),
                sessionId: sessionIdToUse || 'default',
              };
              
              addTreatmentPlan(treatmentPlan);
              setCurrentTreatmentPlan(treatmentPlan);
              
              if (viewMode === 'chat') {
                setViewMode('split');
              }
            }
          }
        }
      }

      // Update session ID if provided
      if (response.session_id) {
        setCurrentSessionId(response.session_id);
      }

      // Check if this response contains criteria updates or treatment options
      if (response.metadata?.criteria_updated || 
          response.metadata?.treatment_options_provided ||
          response.action === 'criteria_update' ||
          response.action === 'treatment_options_provided' ||
          (response.response && response.response.includes('criteria') && response.response.includes('updated'))) {
        
        logger.log('📊 Criteria or treatment options updated, refreshing document...');
        setLastCriteriaUpdate(new Date());
        
        // If we have a current treatment plan, update it with new criteria
        if (currentTreatmentPlan && (response.session_id || currentSessionId)) {
          const sessionIdToUse = response.session_id || currentSessionId;
          if (sessionIdToUse) {
            await updateDocumentWithLatestCriteria(sessionIdToUse);
          }
        }
      }

      // Log the interaction
      logger.log('Message sent successfully', { userMessage: content, response });

    } catch (error) {
      logger.error('Failed to send message:', error);
      
      const errorMessage: ChatMessage = {
        id: generateShortId(currentSessionId || `error-${Date.now()}`),
        content: 'Sorry, I encountered an error processing your request. Please try again.',
        sender: 'bot',
        timestamp: new Date(),
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUpload = async (files: File[]) => {
    // Logic for image upload
    logger.log('Image upload triggered', files);
    
    // Track image upload
    if (currentSessionId) {
      orthoTrack.imageAnalyzed(files.length, currentSessionId);
    }
  };

  const handleNewSession = async () => {
    try {
      setMessages([]);
      setCurrentTreatmentPlan(null); // Clear the document editor
      
      // Reset the API service session first
      apiService.resetSession();
      
      // Create a new session and set it - this will throw if backend is unavailable
      const newSessionId = await apiService.createSession();
      setCurrentSessionId(newSessionId);
      apiService.setSessionId(newSessionId); // Ensure API service knows about it
      logger.log('New session started:', newSessionId);
    } catch (error: any) {
      logger.error('Failed to create new session:', error);
      // Show error message to user
      const errorMessage: ChatMessage = {
        id: `error-${Date.now()}`,
        content: 'Unable to create a new session. Please check your connection and try again.',
        sender: 'bot',
        timestamp: new Date(),
      };
      setMessages([errorMessage]);
    }
  };

  const handleLoadSession = (sessionId: string, sessionMessages: ChatMessage[]) => {
    setCurrentSessionId(sessionId);
    setMessages(sessionMessages);
    // IMPORTANT: Also update the API service session ID
    apiService.setSessionId(sessionId);
    logger.log('Session loaded:', sessionId);
  };

  // Function to update document with latest criteria
  const updateDocumentWithLatestCriteria = async (sessionId: string) => {
    try {
      logger.log('🔄 Updating document with latest criteria...');
      
      // Fetch updated case criteria and treatment plan
      const [treatmentPlanData, caseCriteriaData] = await Promise.all([
        apiService.getTreatmentPlan(sessionId).catch(err => {
          logger.warn('Could not fetch treatment plan:', err);
          return null;
        }),
        apiService.getCaseCriteria(sessionId).catch(err => {
          logger.warn('Could not fetch case criteria:', err);
          return null;
        })
      ]);

      if (treatmentPlanData || caseCriteriaData) {
        // Generate updated document content
        const updatedContent = formatTreatmentPlanFromAPI(
          treatmentPlanData || {}, 
          sessionId, 
          caseCriteriaData
        );
        
        // Update the current treatment plan
        if (currentTreatmentPlan) {
          updateTreatmentPlan(currentTreatmentPlan.id, updatedContent);
          logger.log('✅ Document updated with latest criteria');
        } else {
          // Create new treatment plan if none exists
          const treatmentPlanId = generateShortId(sessionId || `plan-${Date.now()}`);
          const newTreatmentPlan = {
            id: treatmentPlanId,
            title: `Treatment Plan - ${new Date().toLocaleDateString()}`,
            content: updatedContent,
            createdAt: new Date(),
            sessionId: sessionId || 'default',
          };
          
          addTreatmentPlan(newTreatmentPlan);
          setCurrentTreatmentPlan(newTreatmentPlan);
          
          // Switch to split view if not already
          if (viewMode === 'chat') {
            setViewMode('split');
          }
        }
      }
    } catch (error) {
      logger.error('Failed to update document with criteria:', error);
    }
  };

  // Helper function to format treatment plan content for document (fallback)
  const formatTreatmentPlanForDocument = (content: string, response: any) => {
    const sessionId = response.session_id || currentSessionId || 'N/A';
    const hashedSessionId = hashSessionId(sessionId);
    const currentDate = new Date().toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
    
    // Convert markdown-style content to HTML
    let htmlContent = content
      // Convert headers
      .replace(/^### (.+)$/gm, '<h3>$1</h3>')
      .replace(/^## (.+)$/gm, '<h2>$1</h2>')
      .replace(/^# (.+)$/gm, '<h1>$1</h1>')
      // Convert bold text
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      // Convert bullet points
      .replace(/^[\*\-•] (.+)$/gm, '<li>$1</li>')
      // Wrap consecutive li elements in ul
      .replace(/(<li>.*<\/li>\n?)+/g, (match) => `<ul>${match}</ul>`)
      // Convert numbered lists
      .replace(/^\d+\. (.+)$/gm, '<li>$1</li>')
      // Convert line breaks
      .replace(/\n/g, '<br/>');
    
    // Build the complete document
    let formattedContent = `
      <h1 style="text-align: center; color: #2563eb;">OrthoMind Treatment Plan</h1>
      <div style="margin-bottom: 20px;">
        <p><strong>Date:</strong> ${currentDate}</p>
        <p><strong>Session ID:</strong> ${hashedSessionId}</p>
      </div>
      <hr style="margin: 20px 0; border-top: 1px solid #e5e7eb;">
      ${htmlContent}
    `;
    
    return formattedContent;
  };

  // Helper function to format treatment plan from API response
  const formatTreatmentPlanFromAPI = (planData: any, sessionId: string, caseCriteriaData?: any) => {
    // Track treatment plan generation
    orthoTrack.treatmentPlanGenerated(sessionId, caseCriteriaData);
    
    const currentDate = new Date().toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
    
    let formattedContent = `
      <h1 style="text-align: center; color: #2563eb;">OrthoMind Treatment Plan</h1>
      <div style="margin-bottom: 20px;">
        <p><strong>Date:</strong> ${currentDate}</p>
        <p><strong>Case ID:</strong> ${sessionId}</p>
      </div>
      <hr style="margin: 20px 0; border-top: 1px solid #e5e7eb;">
    `;

    // Add Case Criteria section (Criteria Analysis) if available
    if (caseCriteriaData) {
      const formattedCriteria = formatCaseCriteria(caseCriteriaData);
      formattedContent += `
        <h2>Criteria Analysis</h2>
        <ul style="list-style-type: none; padding-left: 0;">
      `;
      
      // Add each criteria field in the specified order
      const criteriaOrder = [
        'Malocclusion Type',
        'All Permanent Teeth Present',
        'Crowding',
        'Maxillary Transverse Constriction (Premolar Region)',
        'Spee Curve (Lower Arch)',
        'Correction Type Indicated',
        'Upper Midline Alignment',
        'Distalization Amount',
        'Distalization Side'
      ];
      
      criteriaOrder.forEach(field => {
        const value = formattedCriteria[field] || 'Not specified';
        formattedContent += `<li style="margin-bottom: 8px;"><strong>${field}:</strong> ${value}</li>`;
      });
      
      formattedContent += `</ul>`;
    }

    // Add Treatment Decisions (combined_decisions) if available
    if (planData && planData.treatment_decisions && planData.treatment_decisions.combined_decisions) {
      formattedContent += `
        <h2>Treatment Decisions</h2>
        <ul>
      `;
      planData.treatment_decisions.combined_decisions.forEach((decision: string) => {
        formattedContent += `<li>${decision}</li>`;
      });
      formattedContent += `</ul>`;
    }

    // Add Invisalign Treatment Plan if has_invisalign_plan is true
    if (planData && planData.has_invisalign_plan && planData.invisalign_plan) {
      formattedContent += `
        <h2>Invisalign Treatment Plan</h2>
      `;
      
      // Check if invisalign_plan is directly available
      if (Array.isArray(planData.invisalign_plan) && planData.invisalign_plan.length > 0) {
        formattedContent += `<ol>`;
        planData.invisalign_plan.forEach((step: string) => {
          formattedContent += `<li>${step}</li>`;
        });
        formattedContent += `</ol>`;
      }
      // Otherwise check in additional_data.treatment_dict_list for invisalign_plan
      else if (planData.additional_data && planData.additional_data.treatment_dict_list) {
        const allInvisalignSteps: string[] = [];
        planData.additional_data.treatment_dict_list.forEach((treatment: any) => {
          if (treatment.invisalign_plan && Array.isArray(treatment.invisalign_plan)) {
            treatment.invisalign_plan.forEach((step: string) => {
              if (step && step !== 'TODO') {
                allInvisalignSteps.push(step);
              }
            });
          }
        });
        
        if (allInvisalignSteps.length > 0) {
          formattedContent += `<ol>`;
          allInvisalignSteps.forEach(step => {
            formattedContent += `<li>${step}</li>`;
          });
          formattedContent += `</ol>`;
        }
      }
    }

    // Add Clinical Tips if has_clinical_tips is true
    if (planData && planData.has_clinical_tips && planData.clinical_tips) {
      formattedContent += `<h2>Clinical Tips</h2>`;
      
      // Handle different formats of clinical tips
      let tips: string[] = [];
      
      if (Array.isArray(planData.clinical_tips)) {
        tips = planData.clinical_tips;
      } else if (typeof planData.clinical_tips === 'string') {
        // Split string clinical tips by newlines or bullet points
        tips = planData.clinical_tips
          .split(/\n+|(?=[-•*]\s)/)
          .map((tip: string) => tip.replace(/^[-•*]\s*/, '').trim())
          .filter((tip: string) => tip.length > 0);
      }
      
      // If we have tips from direct field, use numbered list
      if (tips.length > 0) {
        formattedContent += `<ol style="margin-left: 20px;">`;
        tips.forEach((tip: string) => {
          formattedContent += `<li style="margin-bottom: 8px;">${tip}</li>`;
        });
        formattedContent += `</ol>`;
      }
      // Otherwise check in additional_data.treatment_dict_list for clinical tips
      else if (planData.additional_data && planData.additional_data.treatment_dict_list) {
        const allClinicalTips: string[] = [];
        planData.additional_data.treatment_dict_list.forEach((treatment: any) => {
          if (treatment['clinical tips']) {
            allClinicalTips.push(`<strong>${treatment.label}:</strong> ${treatment['clinical tips']}`);
          }
        });
        
        if (allClinicalTips.length > 0) {
          formattedContent += `<ol style="margin-left: 20px;">`;
          allClinicalTips.forEach(tip => {
            formattedContent += `<li style="margin-bottom: 8px;">${tip}</li>`;
          });
          formattedContent += `</ol>`;
        }
      }
    }

    // Remove Treatment Phases section - not needed in document

    return formattedContent;
  };

  const MainApp = () => (
    <div className="flex h-screen bg-gray-50">
      {/* API Logs Modal */}
      {showApiLogs && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="font-semibold">API Logs</h3>
              <button
                onClick={() => setShowApiLogs(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              <pre className="text-xs font-mono whitespace-pre-wrap">
                {apiLogs.join('\n') || 'No logs yet'}
              </pre>
            </div>
          </div>
        </div>
      )}

      {/* Sidebar */}
      <Sidebar
        onHome={() => {
          // Reset to home view
          setShowAskQuestions(false);
          setShowScanPage(false);
          setShowDeepResearch(false);
          setShowScanHistory(false);
          setShowTreatmentDemo(false);
          setShowTreatmentPlan(false);
          // Reset messages to show welcome
          setMessages([]);
          setCurrentTreatmentPlan(null); // Clear the document editor
          
          // Reset API service session and clear current session
          apiService.resetSession();
          setCurrentSessionId(null);
          logger.log('Returned to home - session will be created on next action');
        }}
        onNewSession={handleNewSession}
        onLoadSession={handleLoadSession}
        currentSessionId={currentSessionId}
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        onShowTreatmentDemo={() => {
          // Track feature selection
          orthoTrack.featureSelected('treatment_plan');
          orthoTrack.sessionStarted('treatment_plan');
          
          // Show treatment plan regular chat
          setShowAskQuestions(false);
          setShowScanPage(false);
          setShowDeepResearch(false);
          setShowScanHistory(false);
          setShowTreatmentDemo(false);
          setShowTreatmentPlan(true);
          // Clear existing messages and session when starting treatment plan
          setMessages([]);
          setCurrentTreatmentPlan(null);
          
          // Don't create session here - let Welcome Screen or first message create it
          setCurrentSessionId(null);
          logger.log('Treatment plan view activated - session will be created on action');
        }}
        onTreatmentDemo={() => {
          // Show the actual treatment demo
          setShowAskQuestions(false);
          setShowScanPage(false);
          setShowDeepResearch(false);
          setShowScanHistory(false);
          setShowTreatmentPlan(false);
          setShowTreatmentDemo(true);
        }}
        onShowScanHistory={() => setShowScanHistory(true)}
        onShowScanAnalysis={() => setShowScanPage(true)}
        onShowDeepResearch={() => setShowDeepResearch(true)}
        onShowAskQuestions={() => {
          setShowAskQuestions(true);
          // Clear document when switching to Ask Questions
          setCurrentTreatmentPlan(null);
          // Don't create session here - let Welcome Screen or first message create it
          setCurrentSessionId(null);
          logger.log('Ask Questions view activated - session will be created on action');
        }}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          user={user}
          backendConnected={backendConnected}
          onShowApiLogs={() => setShowApiLogs(true)}
        />
        
        <div className="flex-1 flex overflow-hidden">
          {/* Conditional rendering based on active view */}
          {showScanPage ? (
            <div className="flex-1 w-full">
              <ScanAnalysisPage onClose={() => setShowScanPage(false)} />
            </div>
          ) : showDeepResearch ? (
            <div className="flex-1 w-full">
              <DeepResearch onClose={() => setShowDeepResearch(false)} />
            </div>
          ) : showTreatmentDemo ? (
            <div className="flex-1 w-full">
              <TreatmentPlanDemo onClose={() => setShowTreatmentDemo(false)} />
            </div>
          ) : showTreatmentPlan ? (
            // Show regular chat interface for Treatment Plan
            <div className="flex flex-1 w-full">
              {/* Chat Interface */}
              {(viewMode === 'chat' || viewMode === 'split') && (
                <div className={viewMode === 'split' ? 'w-1/2 border-r' : 'flex-1'}>
                  <ChatInterface
                    messages={messages}
                    onSendMessage={sendMessage}
                    onImageUpload={handleImageUpload}
                    isLoading={isLoading}
                    sessionId={currentSessionId}
                    autoShowDraftPlan={true}
                    onResetChat={handleNewSession}
                  />
                </div>
              )}
              
              {/* Document Editor */}
              {(viewMode === 'document' || viewMode === 'split') && (
                <div className={viewMode === 'split' ? 'w-1/2' : 'flex-1'}>
                  <DocumentEditor />
                </div>
              )}
            </div>
          ) : showScanHistory ? (
            <ScanHistory 
              onClose={() => setShowScanHistory(false)}
              onSelectSession={(sessionId) => {
                logger.log('Selected session:', sessionId);
                setShowScanHistory(false);
              }}
            />
          ) : showAskQuestions ? (
            <div className="flex-1 w-full">
              <AskQuestionsInterface onClose={() => setShowAskQuestions(false)} />
            </div>
          ) : messages.length === 0 && !currentSessionId && !showTreatmentPlan ? (
            // Show Welcome Screen when no messages and no active session and not showing treatment plan
            <div className="flex-1 w-full">
              <WelcomeScreen
                onAskQuestions={async () => {
                  try {
                    setShowAskQuestions(true);
                    // Only create session if we don't have one
                    if (!currentSessionId) {
                      const newSessionId = await apiService.createSession();
                      setCurrentSessionId(newSessionId);
                      apiService.setSessionId(newSessionId); // Ensure API service knows about it
                      logger.log('New session for Ask Questions from Welcome:', newSessionId);
                    } else {
                      apiService.setSessionId(currentSessionId); // Ensure API service uses existing session
                      logger.log('Using existing session for Ask Questions:', currentSessionId);
                    }
                  } catch (error: any) {
                    logger.error('Failed to create session for Ask Questions:', error);
                    alert('Unable to start Q&A session. Please check your connection and try again.');
                    setShowAskQuestions(false);
                  }
                }}
                onTreatmentPlan={async () => {
                  try {
                    setShowTreatmentPlan(true);
                    // Only create session if we don't have one
                    if (!currentSessionId) {
                      const newSessionId = await apiService.createSession();
                      setCurrentSessionId(newSessionId);
                      apiService.setSessionId(newSessionId); // Ensure API service knows about it
                      logger.log('New session for Treatment Plan from Welcome:', newSessionId);
                    } else {
                      apiService.setSessionId(currentSessionId); // Ensure API service uses existing session
                      logger.log('Using existing session for Treatment Plan:', currentSessionId);
                    }
                  } catch (error: any) {
                    logger.error('Failed to create session for Treatment Plan:', error);
                    alert('Unable to start treatment planning. Please check your connection and try again.');
                    setShowTreatmentPlan(false);
                  }
                }}
                onScanAnalysis={async () => {
                  setShowScanPage(true);
                  // Only create session if we don't have one
                  if (!currentSessionId) {
                    const newSessionId = await apiService.createSession();
                    setCurrentSessionId(newSessionId);
                    apiService.setSessionId(newSessionId); // Ensure API service knows about it
                    logger.log('New session for Scan Analysis from Welcome:', newSessionId);
                  } else {
                    apiService.setSessionId(currentSessionId); // Ensure API service uses existing session
                    logger.log('Using existing session for Scan Analysis:', currentSessionId);
                  }
                }}
                {...(featureConfig.showDeepResearch && { 
                  onDeepResearch: async () => {
                    setShowDeepResearch(true);
                    // Only create session if we don't have one
                    if (!currentSessionId) {
                      const newSessionId = await apiService.createSession();
                      setCurrentSessionId(newSessionId);
                      apiService.setSessionId(newSessionId); // Ensure API service knows about it
                      logger.log('New session for Deep Research from Welcome:', newSessionId);
                    } else {
                      apiService.setSessionId(currentSessionId); // Ensure API service uses existing session
                      logger.log('Using existing session for Deep Research:', currentSessionId);
                    }
                  }
                })}
              />
            </div>
          ) : (
            <div className="flex flex-1 w-full">
              {/* Chat Interface */}
              {(viewMode === 'chat' || viewMode === 'split') && (
                <div className={viewMode === 'split' ? 'w-1/2 border-r' : 'flex-1'}>
                  <ChatInterface
                    messages={messages}
                    onSendMessage={sendMessage}
                    onImageUpload={handleImageUpload}
                    isLoading={isLoading}
                    sessionId={currentSessionId}
                    onResetChat={handleNewSession}
                  />
                </div>
              )}
              
              {/* Document Editor */}
              {(viewMode === 'document' || viewMode === 'split') && (
                <div className={viewMode === 'split' ? 'w-1/2' : 'flex-1'}>
                  <DocumentEditor />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return <MainApp />;
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <TreatmentPlanProvider>
          <Routes>
            {/* Public routes */}
            <Route path="/signin" element={<SignIn />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            
            {/* Protected routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <AppContent />
                </ProtectedRoute>
              }
            />
            
            {/* Redirect any unknown routes to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Analytics />
        </TreatmentPlanProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;